/**
 * CR-7264: a supplier group's combined figures, above its member sites.
 *
 * Rendered as a labelled band spanning the table rather than bare numbers in
 * the member columns. Two reasons. The table is 2,600px wide and scrolls
 * horizontally, so a group's PPM in its own column would sit off-screen for a
 * user looking at the group name; labelled figures read without scrolling. And
 * a band cannot silently misalign the day somebody inserts a column.
 *
 * Every ratio here is recomputed from the group's summed totals — see
 * groupAggregate.ts for why averaging member percentages is wrong.
 */
import { type SupplierGroupAggregate } from './groupAggregate';
import { formatCurrency } from './utils';

interface GroupTotalRowProps {
  group: SupplierGroupAggregate;
  colSpan: number;
  qualityLabel: string;
  currency: string;
}

export function GroupTotalRow({ group, colSpan, qualityLabel, currency }: GroupTotalRowProps) {
  const num = (v: number) => v.toLocaleString();
  const ratio = (v: number | null, digits = 2) =>
    v === null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: digits });
  const money = (v: number) => formatCurrency(String(v), currency);

  const figures: Array<[string, string]> = [
    ['Qty Received', num(group.qtyReceived)],
    ['Qty Rejected', num(group.rejectQty)],
    [`Total ${qualityLabel}`, ratio(group.totalPpm)],
    ['No. of NCRs', num(group.ncrCount)],
    // Customer review 11 Sep (msg 10): the group's cost too — money sums.
    ['Total Value', money(group.totalValue)],
    ['Total Cost of Quality', money(group.totalCostOfQuality)],
    ['PO Lines', num(group.poLineCount)],
    ['OTIF', group.otifPercentage === null ? '—' : `${ratio(group.otifPercentage, 1)}%`],
    ['Arrears', num(group.arrearsCount)],
    ['On time', group.onTimePercentage === null ? '—' : `${ratio(group.onTimePercentage, 1)}%`],
  ];
  const memberWord = group.members.length === 1 ? 'supplier' : 'suppliers';

  return (
    <tr className="border-b-2 bg-muted/60" data-testid={`group-total-${group.groupId}`}>
      <td colSpan={colSpan} className="p-3 sticky left-0 bg-muted/60 z-10">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="font-semibold text-sm">
            {group.groupName}
            <span className="ml-2 font-normal text-xs text-muted-foreground">
              group total · {group.members.length} {memberWord}
            </span>
          </span>
          {figures.map(([label, value]) => (
            <span key={label} className="text-xs whitespace-nowrap">
              <span className="text-muted-foreground">{label}: </span>
              <span className="font-medium tabular-nums">{value}</span>
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}
